import { DateTimeFormat, formatEnum } from '../../../../../formatter.utils.ts';
import { dateReadableValueGetter, getRowDataForRowGroupColumn } from '../../../../../technical/grids/agGrid.utils.tsx';
import {
  type IImportPositionsHistoryQuery,
  IProcessStatus,
  useImportPositionsHistoryQuery,
} from '../../../../../../generated/graphql.tsx';
import Loader from '../../../../../technical/Loader/Loader.tsx';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { Box, Stack, Tooltip } from '@mui/joy';
import { UserSettings } from '../../../../../management/UserSettings.types.ts';
import GAgGridPresets from 'components/technical/grids/GAgGridPresets.tsx';
import { presets } from './importPositionsListPresets.ts';
import { ErrorView } from 'components/technical/errors/ErrorView.tsx';

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

type RowData = IImportPositionsHistoryQuery['portfolio']['imports'][number];

const getErrorLinesAsText = (rowData: RowData): string[] => {
  const errors = rowData.result?.errors;
  if (!errors || errors.length === 0) {
    return [];
  }

  return errors.map((err) => `Line ${err.lineNo}: ${err.message}`);
};

const columns: ColDef<RowData>[] = [
  {
    headerName: 'Upload time',
    type: 'dateColumn',
    initialSort: 'desc',
    valueGetter: (params: ValueGetterParams<RowData>): string | undefined => {
      // backend returns string instead of arrow/date
      return params.data
        ? dateReadableValueGetter(params.data.remoteFile.uploadTime, DateTimeFormat.DateTime)
        : undefined;
    },
  },
  {
    headerName: 'Status',
    type: 'textColumn',
    field: 'status',
    cellRenderer: (params: ICellRendererParams<RowData>): ReactElement | undefined => {
      const rowData = getRowDataForRowGroupColumn(params);
      if (!rowData) {
        return undefined;
      }

      if (isInProgress(rowData.status)) {
        return (
          <Stack height={'100%'} alignItems={'center'} direction={'row'}>
            <Loader variant={'small'} appearance="container" />
          </Stack>
        );
      }

      return <div>{formatEnum(rowData.status)}</div>;
    },
  },
  {
    headerName: 'Processed lines',
    type: ['numericColumn', 'extendedNumericColumn'],
    field: 'result.processedLines',
  },
  {
    headerName: 'Total errors',
    type: ['numericColumn', 'extendedNumericColumn'],
    field: 'result.totalErrors',
  },
  {
    headerName: 'Errors',
    type: ['textColumn'],
    valueGetter: (params: ValueGetterParams<RowData>): string | undefined => {
      const rowData = params.data;
      if (!rowData) {
        return undefined;
      }

      return getErrorLinesAsText(rowData).join(', ');
    },
    cellRenderer: (params: ICellRendererParams<RowData>): ReactNode => {
      const rowData = getRowDataForRowGroupColumn(params);
      if (!rowData) {
        return undefined;
      }

      const lines = getErrorLinesAsText(rowData);
      if (lines.length === 0) {
        return undefined;
      }

      if (lines.length === 1) {
        return lines[0];
      }

      const [firstError, ...rest] = lines;

      return (
        <Tooltip
          title={
            <Stack>
              {rest.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </Stack>
          }
        >
          <Box>{firstError}</Box>
        </Tooltip>
      );
    },
  },
];

const isInProgress = (status: IProcessStatus): boolean => {
  return [IProcessStatus.Pending, IProcessStatus.InProgress].includes(status);
};

export const ImportPositionsList = (props: {
  portfolioDefinitionId: string;
}): ReactElement => {
  const [refreshImports, setRefreshImports] = useState(true);
  const historyQuery = useImportPositionsHistoryQuery({
    variables: {
      portfolioDefinitionId: props.portfolioDefinitionId,
    },
    pollInterval: refreshImports ? 5000 : 0,
  });

  const imports = historyQuery.data?.portfolio.imports;
  useEffect(() => {
    if (!imports) {
      return;
    }

    const hasPending = imports.some((imp) => isInProgress(imp.status));
    setRefreshImports(hasPending);
  }, [imports]);

  return (
    <Stack direction={'column'} justifyContent={'space-between'} flexGrow={1}>
      {historyQuery.error && <ErrorView cause={historyQuery.error} variant="inline" />}
      {!historyQuery.called && historyQuery.loading && <Loader />}
      <GAgGridPresets<RowData>
        presetSettingsKey={UserSettings.ImportPortfolioPositionsPresets}
        defaultPresets={presets}
        rowData={historyQuery.data?.portfolio.imports ?? historyQuery.previousData?.portfolio.imports}
        minHeight={500}
        defaultColDef={defaultColDef}
        sideBar={{ toolPanels: ['filters'] }}
        autoSizeStrategy={{ type: 'fitCellContents' }}
        columnDefs={columns}
      />
    </Stack>
  );
};
